"use client";

import { useEffect, useRef } from "react";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";

/**
 * Pixel mosaic of the anonymised traders (hero + final). Port of the prototype engine
 * (public/previews/iq-top1-files-lp, "os 4 traders em mosaico de pixels"): every photo is sampled into blocks,
 * the blocks shimmer, the mouse trail drags and brightens them, a click coarsens the blocks and lets them settle,
 * and on first paint the mosaic resolves from coarse to fine. Geometry comes from the Figma frame
 * (`frameW` × `frameH` px); the canvas scales with its box, so 1 Figma px = box width / frameW.
 */
export interface QeMosaicPerson {
  src?: string;
  /** Figma rect of the photo inside the frame, px. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Block edge in Figma px (Figma's pre-pixelated layers use 34 image px blocks). */
  block: number;
  /** Combined opacity of the Figma layers. */
  alpha: number;
}

type Cell = { x: number; y: number; a: number; n: number; r: number; g: number; b: number };
type Grid = { B: number; cells: Cell[] };

const LEVELS = [1, 1.3, 1.65, 2, 2.35];

function withWidth(src: string, w: number) {
  const clean = stegaClean(src);
  return clean.includes("cdn.sanity.io") ? `${clean}${clean.includes("?") ? "&" : "?"}w=${Math.ceil(w)}` : clean;
}

function load(src: string, w: number) {
  return new Promise<HTMLImageElement | null>((resolve) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = () => resolve(null);
    im.src = withWidth(src, w);
  });
}

function sample(im: HTMLImageElement, p: QeMosaicPerson, B: number, frameH: number): Grid {
  const cols = Math.ceil(p.w / B);
  const rows = Math.ceil(p.h / B);
  const c = document.createElement("canvas");
  c.width = cols;
  c.height = rows;
  const x = c.getContext("2d", { willReadFrequently: true });
  if (!x) return { B, cells: [] };
  x.imageSmoothingQuality = "high";
  x.drawImage(im, 0, 0, cols, rows);
  const d = x.getImageData(0, 0, cols, rows).data;
  const cells: Cell[] = [];
  for (let r = 0; r < rows; r++) {
    for (let q = 0; q < cols; q++) {
      const o = (r * cols + q) * 4;
      if (d[o + 3] < 90) continue;
      const y = p.y + r * B;
      if (y > frameH) continue;
      const v = r / rows;
      cells.push({ x: p.x + q * B, y, a: (d[o + 3] / 255) * Math.min(1, (1 - v) / 0.45), n: ((q * 7919 + r * 104729) % 628) / 100, r: d[o], g: d[o + 1], b: d[o + 2] });
    }
  }
  return { B, cells };
}

export default function QeMosaic({ people, frameW, frameH, className, introDelay = 0 }: { people: QeMosaicPerson[]; frameW: number; frameH: number; className?: string; introDelay?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const key = people.map((p) => `${p.src}|${p.x}|${p.y}|${p.w}|${p.h}|${p.block}|${p.alpha}`).join(";");

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const g = cv.getContext("2d");
    if (!g) return;
    const host = (cv.closest("section") as HTMLElement | null) ?? cv.parentElement!;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let W = 0, H = 0, s = 1, dpr = 1, raf = 0, alive = true, vis = true;
    let grids: Grid[][] = [];
    const lay = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = cv.clientWidth;
      H = cv.clientHeight;
      cv.width = Math.round(W * dpr);
      cv.height = Math.round(H * dpr);
      s = W / frameW;
    };
    lay();
    const ro = new ResizeObserver(() => { lay(); if (reduce) draw(performance.now()); });
    ro.observe(cv);

    const trail: { x: number; y: number; dx: number; dy: number }[] = [];
    let lx: number | null = null, ly: number | null = null, burst: number | null = null;
    const onMove = (e: PointerEvent) => {
      const b = cv.getBoundingClientRect();
      const x = e.clientX - b.left, y = e.clientY - b.top;
      if (lx !== null && ly !== null) {
        const dx = x - lx, dy = y - ly;
        if (Math.abs(dx) + Math.abs(dy) > 1) { trail.unshift({ x, y, dx, dy }); if (trail.length > 24) trail.length = 24; }
      }
      lx = x; ly = y;
    };
    const onLeave = () => { lx = ly = null; };
    const onDown = (e: PointerEvent) => { if ((e.target as HTMLElement).closest("a,button")) return; burst = performance.now(); };
    if (!reduce) {
      host.addEventListener("pointermove", onMove);
      host.addEventListener("pointerleave", onLeave);
      host.addEventListener("pointerdown", onDown);
    }
    const io = new IntersectionObserver((es) => { vis = es[0]?.isIntersecting ?? true; });
    io.observe(cv);

    let t0 = performance.now() + introDelay;
    function draw(now: number) {
      if (!grids.length) return;
      const t = Math.max(0, (now - t0) / 1000);
      let lvl = reduce ? 0 : t < 1.4 ? 4 * (1 - t / 1.4) : 0;
      if (burst !== null) {
        const q = (now - burst) / 1000;
        lvl = Math.max(lvl, q < 0.25 ? (4 * q) / 0.25 : Math.max(0, 4 * (1 - (q - 0.25) / 1.6)));
        if (q > 1.9) burst = null;
      }
      const L = Math.max(0, Math.min(LEVELS.length - 1, Math.round(lvl)));
      const tick = reduce ? 0 : Math.floor(t * 9);
      g!.setTransform(dpr, 0, 0, dpr, 0, 0);
      g!.clearRect(0, 0, W, H);
      for (let i = trail.length - 1; i >= 0; i--) {
        const q = trail[i];
        q.dx *= 0.86; q.dy *= 0.86; q.x += q.dx * 0.15; q.y += q.dy * 0.15;
        if (Math.abs(q.dx) + Math.abs(q.dy) < 0.05) trail.splice(i, 1);
      }
      grids[L].forEach((gr, k) => {
        const p = people[k];
        if (!gr) return;
        const B = gr.B * s;
        for (const c of gr.cells) {
          const X0 = c.x * s, Y0 = c.y * s;
          let ddx = 0, ddy = 0;
          for (let j = 0; j < trail.length; j++) {
            const q = trail[j], dx = X0 - q.x, dy = Y0 - q.y, d2 = dx * dx + dy * dy;
            if (d2 > 12000 * s * s) continue;
            const w = (1 - Math.sqrt(d2) / (110 * s)) * (1 - j / trail.length);
            if (w <= 0) continue;
            ddx += q.dx * w; ddy += q.dy * w;
          }
          const fl = reduce ? 1 : 0.82 + 0.18 * Math.sin(c.n + tick * 1.7) * (Math.sin(c.n * 3 + tick) > 0 ? 1 : 0.4);
          const kf = Math.min(1.25, 0.92 * fl);
          g!.globalAlpha = c.a * p.alpha;
          g!.fillStyle = `rgb(${Math.min(255, c.r * kf) | 0},${Math.min(255, c.g * kf) | 0},${Math.min(255, c.b * kf) | 0})`;
          g!.fillRect(X0 + ddx * 1.15, Y0 + ddy * 1.15, B + 0.35, B + 0.35);
        }
      });
      g!.globalAlpha = 1;
    }
    const frame = (now: number) => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      if (vis) draw(now);
    };

    Promise.all(people.map((p) => (p.src ? load(p.src, p.w * 1.5) : Promise.resolve(null)))).then((ims) => {
      if (!alive) return;
      grids = LEVELS.map((m) => people.map((p, k) => (ims[k] ? sample(ims[k]!, p, p.block * m, frameH) : null)).filter(Boolean) as Grid[]);
      // keep person index aligned with grids when an image failed
      const okIdx = people.map((_, k) => (ims[k] ? k : -1)).filter((k) => k >= 0);
      people = okIdx.map((k) => people[k]);
      t0 = performance.now() + introDelay;
      if (reduce) draw(performance.now());
      else raf = requestAnimationFrame(frame);
    });

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
      host.removeEventListener("pointerdown", onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, frameW, frameH, introDelay]);

  return <canvas ref={ref} aria-hidden="true" className={cn("pointer-events-none absolute inset-0 h-full w-full", className)} />;
}
